import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../../common/types';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { Cover } from '../components/primitives/Cover';
import { SMLButton } from '../components/primitives/SMLButton';
import { catalogArtworkSrc } from '../../smartLinks/catalog/catalogArtwork';
import { formatCatalogDay } from '../../smartLinks/catalog/catalogDates';
import { artistHomeMessages as messages } from './artistHomeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Home; each part uses its own slice of it */
import styles from './ArtistHome.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogLatestReleaseProps {
  item: CatalogItem;
  onCreateLink: (item: CatalogItem) => void;
}

/** The latest release with its smartlink status, and "Create smartlink" when it has none (design a1). */
const CatalogLatestReleaseComponent: React.FC<CatalogLatestReleaseProps> = ({ item, onCreateLink }) => {
  const intl = useIntl();
  const create = useCallback(() => onCreateLink(item), [item, onCreateLink]);

  const date = item.releaseDate === null ? null : formatCatalogDay(intl, item.releaseDate);
  let released: string | null = date;
  if (date !== null && item.releaseType !== null) {
    released = intl.formatMessage(messages.catalogReleased, { type: item.releaseType, date });
  }

  const hasLink = item.smartLinkId !== null && item.smartLinkStatus !== null;
  let chip = (
    <span className={clsx(styles.chip, styles.chipWarn)}>{intl.formatMessage(messages.catalogNoSmartlink)}</span>
  );
  if (hasLink) {
    chip =
      item.smartLinkStatus === 'published' ? (
        <span className={clsx(styles.chip, styles.chipOk)}>{intl.formatMessage(messages.catalogSmartlinkLive)}</span>
      ) : (
        <span className={clsx(styles.chip, styles.chipMute)}>{intl.formatMessage(messages.catalogSmartlinkDraft)}</span>
      );
  }

  return (
    <>
      <div className={styles.sectionKey}>{intl.formatMessage(messages.catalogLatest)}</div>
      <div className={styles.release}>
        <Cover src={catalogArtworkSrc(item, 52)} label={item.title} size={52} radius={10} />
        <div className={styles.releaseName}>
          <b>{item.title}</b>
          {released !== null && <small>{released}</small>}
        </div>
        {chip}
      </div>
      {!hasLink && (
        <div>
          <SMLButton variant="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={create}>
            {intl.formatMessage(messages.catalogCreateSmartlink)}
          </SMLButton>
        </div>
      )}
    </>
  );
};

export const CatalogLatestRelease = TypedMemo(CatalogLatestReleaseComponent);
